import { Campo, clasesCon, limpiarDecimal } from './Field';
import { Segmentado } from './Segmentado';
import { leerDecimal } from '@/domain/numbers';
import {
  equivalenciaLongitud,
  equivalenciaPeso,
  UNIDADES_LONGITUD,
  UNIDADES_PESO,
  type UnidadLongitud,
  type UnidadPeso,
  type UsoPeso,
} from '@/domain/units';

interface PropsBase {
  etiqueta: string;
  /** Texto tal como se escribe; se maneja como texto para poder teclear "70.". */
  valor: string;
  onCambio: (valor: string) => void;
  ayuda?: string;
  error?: string | null;
  placeholder?: string;
  autoFocus?: boolean;
}

interface PropsPeso extends PropsBase {
  magnitud: 'peso';
  unidad: UnidadPeso;
  onCambioUnidad: (unidad: UnidadPeso) => void;
  unidadPreferida: UnidadPeso;
  uso?: UsoPeso;
}

interface PropsLongitud extends PropsBase {
  magnitud: 'longitud';
  unidad: UnidadLongitud;
  onCambioUnidad: (unidad: UnidadLongitud) => void;
  unidadPreferida: UnidadLongitud;
}

/**
 * Campo numérico con selector de unidad, kg | lb o cm | in (tarea 5.1).
 *
 * - El selector siempre está visible e independiente de la unidad preferida (Req. 5.2).
 * - Si la unidad elegida no es la preferida, debajo aparece la equivalencia en
 *   vivo, por ejemplo "= 70.0 kg" (Req. 5.3).
 * - Teclado decimal del iPhone; acepta punto o coma (Req. 5.9).
 *
 * Nunca cambia la unidad ni el valor por su cuenta (Req. 5.10): solo informa.
 */
export function UnitField(props: PropsPeso | PropsLongitud) {
  const { etiqueta, valor, onCambio, ayuda, error, placeholder, autoFocus } = props;
  const numero = leerDecimal(valor);

  const equivalencia =
    numero === null
      ? null
      : props.magnitud === 'peso'
        ? equivalenciaPeso(numero, props.unidad, props.unidadPreferida, props.uso ?? 'corporal')
        : equivalenciaLongitud(numero, props.unidad, props.unidadPreferida);

  const selector =
    props.magnitud === 'peso' ? (
      <Segmentado
        etiqueta={`Unidad de ${etiqueta.toLowerCase()}`}
        valor={props.unidad}
        opciones={UNIDADES_PESO.map((u) => ({ valor: u, etiqueta: u }))}
        onCambio={props.onCambioUnidad}
      />
    ) : (
      <Segmentado
        etiqueta={`Unidad de ${etiqueta.toLowerCase()}`}
        valor={props.unidad}
        opciones={UNIDADES_LONGITUD.map((u) => ({ valor: u, etiqueta: u }))}
        onCambio={props.onCambioUnidad}
      />
    );

  return (
    <Campo etiqueta={etiqueta} ayuda={ayuda} error={error} extra={equivalencia}>
      {({ id, describedBy, hayError }) => (
        <div className="flex items-center gap-2">
          <input
            id={id}
            type="text"
            inputMode="decimal"
            value={valor}
            onChange={(e) => onCambio(limpiarDecimal(e.target.value))}
            className={`${clasesCon(hayError)} min-w-0 flex-1 text-right tabular-nums`}
            placeholder={placeholder}
            aria-describedby={describedBy}
            aria-invalid={hayError}
            autoFocus={autoFocus}
            autoComplete="off"
          />
          {selector}
        </div>
      )}
    </Campo>
  );
}
